import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../../../store/rootState'
import { IStudyBudget, IBenchmarkData, IBenchmarkFilters } from '../types/studyBudget'
import { DEFAULT_BENCHMARK_FILTERS } from '../config/benchmarkOptions'
import { DeletedPosition } from '../utils/amendmentDiff'
import { generateUUID } from '../utils/uuid'

// NOTE: there is deliberately no `sourceBudget` here. An amendment's diff baseline is
// `budget.source_snapshot`, frozen at amendment creation and delivered on the detail
// payload, so there is nothing to fetch and nothing to cache (issue #20 P0).
export interface BudgetDetailState {
  budget: IStudyBudget | null
  loading: boolean
  error: string | null
  editMode: boolean
  stale: boolean
  viewType: 'list' | 'timeline'
  benchmarks: IBenchmarkData | null
  benchmarkFilters: IBenchmarkFilters
  // True once the user has applied the benchmark filters (even an empty selection),
  // as opposed to the automatic fetch on page load. Gates the benchmark icons.
  benchmarkApplied: boolean
}

const initialState: BudgetDetailState = {
  budget: null,
  loading: false,
  error: null,
  editMode: false,
  stale: false,
  viewType: 'list',
  benchmarks: null,
  benchmarkFilters: DEFAULT_BENCHMARK_FILTERS,
  benchmarkApplied: false,
}

/**
 * Deep-copy a source position and stamp it for re-insertion into an amendment.
 *
 * The copy gets a FRESH id (the original may still be live elsewhere in the amendment)
 * and keeps the original id in its lineage field. Lineage is what the diff engine matches
 * on, so the copy diffs as unchanged against the source instead of as newly added.
 *
 * @param source - Raw source position: an internal role or an external position
 * @param lineageField - `source_role_id` for internal roles, `source_position_id` for external
 * @returns A detached copy carrying a new UUID and the original id as lineage
 * @example
 * const clone = clonePositionForRestore({ id: 'src-1', position_name: 'PM' }, 'source_role_id')
 * clone.source_role_id // 'src-1'
 */
const clonePositionForRestore = (
  source: any,
  lineageField: 'source_role_id' | 'source_position_id',
): any => {
  const clone = JSON.parse(JSON.stringify(source))
  clone.id = generateUUID()
  clone[lineageField] = source.id
  return clone
}

/**
 * Clone a source role for restoration, mirroring exactly what the backend produces when
 * it clones a source budget into an amendment: fresh role UUID, fresh hour_data entry
 * UUIDs, original role id preserved as `source_role_id`.
 *
 * Contract: BudgetProjectSerializer._clone_budget_positions (trialytix/budget/serializers.py)
 *
 * `hour_data` is read directly, without a guard. That is safe by contract, not by luck:
 * budgetPositionRole requires it (issue #20, Tim's analysis 7). It is what a role's hours
 * are MADE of — every producer emits it and the calculation service derives total_hours
 * from it — but the schema had never said so, and a role without it crashed this line with
 * `Cannot read properties of undefined`, taking the budget detail screen down. An EMPTY
 * list is legal and iterates fine; absence is not, and failing loudly on it is intended.
 *
 * @param sourceRole - Raw role from the frozen source snapshot
 * @returns Schema-valid role (see budgetPositionRole in /shared/schemas/budget_schemas.json)
 * @example
 * const role = cloneRoleForRestore({ id: 'src-1', hour_data: [{ id: 'h-1', hours: 8 }] })
 * role.hour_data[0].id !== 'h-1' // true
 */
const cloneRoleForRestore = (sourceRole: any): any => {
  const clone = clonePositionForRestore(sourceRole, 'source_role_id')
  clone.hour_data.forEach((entry: any) => {
    entry.id = generateUUID()
  })
  return clone
}

/**
 * Locate the frozen source category that owned a role, which is the only place the
 * restored position's category identity (id, name, display order) still exists once the
 * amendment has dropped the category along with its last position.
 *
 * @param sourceCategories - `source_snapshot.budget_positions` of the amendment
 * @param sourceRoleId - Id of the deleted source role
 * @returns The source category holding that role
 * @throws TypeError if no source category owns the role
 * @example
 * findSourceCategory([{ id: 3, name: 'Personnel', department_roles: [{ id: 'r1' }] }], 'r1').name // 'Personnel'
 */
const findSourceCategory = (sourceCategories: any[], sourceRoleId: string): any =>
  sourceCategories.find((category) =>
    category.department_roles.some((role: any) => String(role.id) === sourceRoleId),
  )!

/**
 * Find the amendment category corresponding to a source category. Matched by id first —
 * categories are cloned id-and-all into the amendment, so a category RENAMED inside the
 * amendment still matches — and by name second, which catches a category the user
 * recreated by hand after deleting the original.
 *
 * @param categories - The amendment's `budget_positions`
 * @param sourceCategory - The category as it exists in the frozen source
 * @returns The matching amendment category, or undefined when the amendment has none
 * @example
 * findAmendmentCategory([{ id: 3, name: 'Renamed' }], { id: 3, name: 'Personnel' }).name // 'Renamed'
 */
const findAmendmentCategory = (categories: any[], sourceCategory: any): any => {
  const byId = categories.find((category) => String(category.id) === String(sourceCategory.id))
  if (byId) return byId
  return categories.find((category) => category.name === sourceCategory.name)
}

/**
 * Resolve the amendment category a restored role belongs in, recreating it from the source
 * category when the amendment dropped it (every position in it had been deleted).
 *
 * @param categories - The amendment's `budget_positions` (mutated when a category is recreated)
 * @param sourceCategory - The category as it exists in the frozen source
 * @returns The category to push the restored role into
 * @example
 * categoryForRestore([], { id: 3, name: 'Personnel', display_order: 2 }).department_roles // []
 */
const categoryForRestore = (categories: any[], sourceCategory: any): any => {
  const existing = findAmendmentCategory(categories, sourceCategory)
  if (existing) return existing
  // Schema: /shared/schemas/budget_schemas.json#/definitions/budgetCategory
  categories.push({
    id: sourceCategory.id,
    name: sourceCategory.name,
    display_order: sourceCategory.display_order,
    department_roles: [],
  })
  return categories[categories.length - 1]
}

const budgetDetailSlice = createSlice({
  name: 'budgetDetail',
  initialState,
  reducers: {
    fetchBudgetDetailStart: (state) => {
      state.loading = true
      state.error = null
    },
    fetchBudgetDetailSuccess: (state, action: PayloadAction<IStudyBudget>) => {
      state.budget = action.payload
      state.loading = false
      state.error = null
      state.stale = false
    },
    fetchBudgetDetailFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    toggleEditMode: (state) => {
      state.editMode = !state.editMode
    },
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.editMode = action.payload
    },
    updateBudgetDetail: (state, action: PayloadAction<Partial<IStudyBudget>>) => {
      if (state.budget) {
        state.budget = { ...state.budget, ...action.payload }
      }
    },
    clearBudgetDetail: (state) => {
      state.budget = null
      state.loading = false
      state.error = null
      state.editMode = false
      state.stale = false
      state.viewType = 'list'
      state.benchmarks = null
      state.benchmarkFilters = DEFAULT_BENCHMARK_FILTERS
      state.benchmarkApplied = false
    },
    markBudgetDetailAsStale: (state) => {
      state.stale = true
    },
    setBudgetError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
    clearBudgetError: (state) => {
      state.error = null
    },
    setViewType: (state, action: PayloadAction<'list' | 'timeline'>) => {
      state.viewType = action.payload
    },
    setBenchmarkData: (state, action: PayloadAction<IBenchmarkData | null>) => {
      state.benchmarks = action.payload
    },
    setBenchmarkFilters: (state, action: PayloadAction<IBenchmarkFilters>) => {
      state.benchmarkFilters = action.payload
      // Applying the filters — including an empty selection — activates the benchmark.
      state.benchmarkApplied = true
    },
    /**
     * Restore a position that was deleted from an amendment AND saved (issue #20 C4).
     *
     * Once saved, the position is gone from the amendment's JSON: what the table shows is
     * a synthetic ghost row rebuilt from the frozen source by computePositionDiffs. Restoring
     * therefore means re-inserting a copy of the source position, not un-flagging anything.
     *
     * The copy is stamped exactly like the backend's own amendment clone — fresh UUID plus
     * `source_role_id` / `source_position_id` pointing at the original — so the next diff pairs
     * it with its source and reports it unchanged rather than added.
     *
     * @param action.payload - The ghost row from `computePositionDiffs(...).deleted`
     */
    restoreDeletedPosition: (state, action: PayloadAction<DeletedPosition>) => {
      const deleted = action.payload
      const budget = state.budget!
      if (deleted.kind === 'external') {
        budget.external_positions!.push(clonePositionForRestore(deleted.source, 'source_position_id'))
        return
      }
      const sourceCategories = budget.source_snapshot!.budget_positions
      const sourceCategory = findSourceCategory(sourceCategories, String(deleted.source.id))
      const category = categoryForRestore(budget.budget_positions!, sourceCategory)
      category.department_roles.push(cloneRoleForRestore(deleted.source))
    }
  }
})

// Export actions
export const {
  fetchBudgetDetailStart,
  fetchBudgetDetailSuccess,
  fetchBudgetDetailFailure,
  toggleEditMode,
  setEditMode,
  updateBudgetDetail,
  clearBudgetDetail,
  markBudgetDetailAsStale,
  setBudgetError,
  clearBudgetError,
  setViewType,
  setBenchmarkData,
  setBenchmarkFilters,
  restoreDeletedPosition,
} = budgetDetailSlice.actions

// Export reducer
export const budgetDetailReducer = budgetDetailSlice.reducer

// Selectors
export const selectBudgetDetail = (state: RootState) => state.budgetDetail.budget
export const selectBudgetDetailLoading = (state: RootState) => state.budgetDetail.loading
export const selectBudgetDetailError = (state: RootState) => state.budgetDetail.error
export const selectBudgetDetailEditMode = (state: RootState) => state.budgetDetail.editMode
export const selectIsBudgetDetailStale = (state: RootState) => state.budgetDetail.stale
export const selectBudgetDetailViewType = (state: RootState) => state.budgetDetail.viewType
export const selectBenchmarks = (state: RootState) => state.budgetDetail.benchmarks
export const selectBenchmarkFilters = (state: RootState) => state.budgetDetail.benchmarkFilters
export const selectBenchmarkApplied = (state: RootState) => state.budgetDetail.benchmarkApplied